import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { queryCache, useQuery } from 'react-query';
import { getScenario } from 'src/api/scenario';
import { useParams } from 'react-router';
import { ProjectionVariant, QKeys } from 'src/api/types';
import Meta from 'src/components/Meta';
import { VariantValues, PageCard, PageChartSkeleton, PageContainer, Spacer, Divider, Poppins } from 'src/common';
import { useGaEventPageView } from '../../utils/gaEvent';
import { Head } from './comps';
import useVersion from 'src/hooks/useVersion';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import {
  AuditSection,
  ExceedanceSection,
  IncidentsSection,
  RiskManagementSection,
  RiskStatementSection,
} from './sections';
import { ObjectKind, useScenarioQuery } from 'src/graphql/_generated';
import ActionsSection from 'src/components/ActionsSection';
import { LABELS } from 'src/components/labels';
import Comments, { CommentsPageLayout } from 'src/components/Comments';

const ChartBox = styled(PageCard)`
  padding-bottom: 0;
`;

const ScenarioPage = () => {
  const { scenarioId, assessmentId } = useParams<{ scenarioId: string; assessmentId: string }>();
  const [isLogarithmic, setIsLogarithmic] = useState(true);
  const [commentsOpen, setCommentsOpen] = useState(false);

  const { versionId, version, isLoading: isVersionLoading } = useVersion({ assessmentId: assessmentId! });
  const { setLocationPaths } = useActionsApp();

  useQuery([QKeys.Scenario, { scenarioId, version: versionId, variant: ProjectionVariant.live }], getScenario);

  const {
    data: { scenario } = {},
    loading: isLoadingScenarioGql,
    error,
  } = useScenarioQuery({ variables: { id: scenarioId!, versionId } });

  const isLoading = isLoadingScenarioGql && !scenario;

  useEffect(() => {
    if (scenario?.assessment) {
      setLocationPaths({
        assessment: { id: scenario.assessment.id, name: scenario.assessment.name },
        scenario: { id: scenario.id, name: scenario.name! },
      });
    }
    return () => {
      setLocationPaths({ assessment: undefined, scenario: undefined });
    };
  }, [scenario]);

  useGaEventPageView('Cydea | Scenario');

  const showAllProjected = !!scenario?.riskManagementPlans
    ?.filter((plan) => plan.control && plan.control.status !== 'Abandoned' && plan.control.status !== 'Retired')
    ?.flatMap((plan) => plan.modifiers)
    ?.filter((modifier) => modifier.type !== 'NOTE_TYPE')?.length;

  const loading = isLoading || isVersionLoading;

  if (loading)
    return (
      <PageContainer>
        <PageChartSkeleton />
      </PageContainer>
    );

  return (
    <>
      <Meta title={`Cydea | ${scenario?.name}`} feedbackScript />
      <CommentsPageLayout>
        <div className="page-content unstyled-scroll">
          <PageContainer data-cy="scenario-page">
            {error && <Poppins color="error">{error.message}</Poppins>}
            {scenario && (
              <>
                <ChartBox>
                  <Head
                    scenario={scenario}
                    isLogarithmic={isLogarithmic}
                    version={version}
                    commentsPanel={{
                      isOpen: commentsOpen,
                      onToggle: setCommentsOpen,
                    }}
                    expectedLossComponent={
                      <VariantValues
                        flat
                        value={
                          showAllProjected
                            ? {
                                base: scenario.baseRisk?.expectedLoss,
                                live: scenario.liveRisk?.expectedLoss,
                                cpmplete: scenario.projectedRisk?.expectedLoss,
                              }
                            : scenario.liveRisk?.expectedLoss
                        }
                        tooltipText={{
                          base: LABELS.riskBase,
                          live: LABELS.riskLive,
                          cpmplete: LABELS.riskComplete,
                        }}
                      />
                    }
                  />
                  <Spacer $px={20} />
                  <Divider />
                  <ExceedanceSection
                    isLogarithmic={isLogarithmic}
                    setIsLogarithmic={setIsLogarithmic}
                    showAllProjected={showAllProjected}
                    scenario={scenario}
                    version={version}
                  />
                  <Divider />
                  <RiskStatementSection scenario={scenario} showAllProjected={showAllProjected} />
                  <Divider />
                  <RiskManagementSection scenario={scenario} />
                  <Divider />
                  <IncidentsSection scenarioId={scenario.id} scenarioName={scenario.name || ''} versionId={versionId} />
                  <Divider />
                  <ActionsSection objectType={ObjectKind.Scenario} objectId={scenario.id} name={scenario.name || ''} />
                  <Divider />
                  <AuditSection versionId={version?.id} scenario={scenario} />
                </ChartBox>
              </>
            )}
          </PageContainer>
        </div>
        {commentsOpen && scenario && (
          <div className="comments-panel open">
            <Comments
              onClose={() => setCommentsOpen(false)}
              eventData={{ objectType: ObjectKind.Scenario, objectId: scenario.id }}
              refetch={() => queryCache.invalidateQueries([QKeys.Audit])}
            />
          </div>
        )}
      </CommentsPageLayout>
    </>
  );
};

export default ScenarioPage;
